/**
 * WaiveGapDialog — record that an evidence gap cannot be filled.
 *
 * WHY A DIALOG AND NOT `window.prompt`. The first cut used `window.prompt`, and
 * on a real browser it silently did nothing: Chrome suppresses page-generated
 * dialogs after a user dismisses one, and blocks them outright in sandboxed
 * frames. The user clicked Waive and got no reason box at all. A native prompt
 * also cannot collect the CATEGORY, cannot validate, and cannot explain what is
 * being recorded — all of which matter here.
 *
 * WHAT A WAIVER IS. Not a dismissal and not a "hide this row". It is an
 * attested claim that a competent person looked and found nothing, which is
 * itself a finding a reader may need to weigh. So it demands a reason, records
 * who and when, and states plainly that it is visible to readers. A one-click
 * "ignore" would convert an unknown into a clean verdict, which is exactly the
 * dishonesty the evidence surfaces exist to prevent.
 *
 * The category is the user's, not ours to infer. "The source holds nothing"
 * (data-unavailable) and "this does not apply to this site" (not-applicable)
 * read identically in a status column and mean very different things to anyone
 * relying on the report.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { X as XIcon, ShieldAlert } from 'lucide-react';
import { Button } from '../../ui/button';
import type { SiteRequirementWaiver } from '../../../types/site';

export type WaiveCategory = SiteRequirementWaiver['category'];

/** The four dispositions, in the order a reviewer actually considers them. */
const CATEGORIES: ReadonlyArray<{ id: WaiveCategory; label: string; help: string }> = [
  {
    id: 'data-unavailable',
    label: 'The source has no record',
    help: 'The provider ran and the source genuinely holds nothing for this site. An absence of record, not an absence of effort.',
  },
  {
    id: 'not-applicable',
    label: 'Does not apply to this site',
    help: 'The requirement is irrelevant here — wrong technology, wrong jurisdiction, wrong project stage.',
  },
  {
    id: 'will-provide',
    label: 'Will be provided later',
    help: 'The evidence exists and is coming. Records the commitment so the gap is tracked rather than forgotten.',
  },
  {
    id: 'accepted-gap',
    label: 'Accepted as a known gap',
    help: 'The evidence is missing, cannot be obtained now, and the project is choosing to proceed knowing that.',
  },
];

/** Shortest reason that can carry meaning to a later reader. */
const MIN_REASON = 10;

export interface WaiveGapDialogProps {
  /** Human label of the gap being waived — shown so the user cannot mis-target. */
  gapLabel: string;
  /** Requirement id the waiver is filed against. */
  requirementId: string;
  /** Pre-selected category. Callers that know the shape of the gap may seed it. */
  defaultCategory?: WaiveCategory;
  onCancel: () => void;
  onConfirm: (reason: string, category: WaiveCategory) => Promise<void> | void;
}

export const WaiveGapDialog: React.FC<WaiveGapDialogProps> = ({
  gapLabel,
  requirementId,
  defaultCategory = 'data-unavailable',
  onCancel,
  onConfirm,
}) => {
  const [category, setCategory] = React.useState<WaiveCategory>(defaultCategory);
  const [reason, setReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const reasonRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useEffect(() => { reasonRef.current?.focus(); }, []);

  const trimmed = reason.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_REASON;
  const canSubmit = trimmed.length >= MIN_REASON && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onConfirm(trimmed, category);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="waive-gap-dialog-title"
      data-testid="waive-gap-dialog"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-4"
      onMouseDown={(e) => {
        // Backdrop dismiss on mousedown only, and never once the user has
        // started writing — losing a typed justification to a stray click is
        // the one thing this dialog must not do.
        if (e.target !== e.currentTarget) return;
        if (trimmed.length > 0) return;
        onCancel();
      }}
      onKeyDown={(e) => { if (e.key === 'Escape' && trimmed.length === 0) onCancel(); }}
    >
      <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex items-start gap-2.5">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
            <div className="min-w-0">
              <h2 id="waive-gap-dialog-title" className="type-section text-text-primary">
                Waive this evidence gap
              </h2>
              <p className="mt-0.5 type-body text-text-muted break-words">{gapLabel}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="shrink-0 rounded-md p-1 text-text-muted hover:bg-state-hover hover:text-text-primary"
            aria-label="Cancel"
            data-testid="waive-gap-cancel-x"
          >
            <XIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="type-body text-text-secondary">
            This is recorded as an attested finding against your name, not a dismissal.
            It stays visible to anyone reading this site&rsquo;s evidence, and it can be
            undone.
          </p>

          <fieldset className="space-y-2">
            <legend className="type-meta mb-1">Why can it not be filled?</legend>
            {CATEGORIES.map(c => (
              <label
                key={c.id}
                className={`flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 transition-colors ${
                  category === c.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-state-hover'
                }`}
                data-testid={`waive-gap-category-${c.id}`}
              >
                <input
                  type="radio"
                  name="waive-category"
                  className="mt-1"
                  checked={category === c.id}
                  onChange={() => setCategory(c.id)}
                />
                <span className="min-w-0">
                  <span className="block type-body font-semibold text-text-primary">{c.label}</span>
                  <span className="block type-body text-text-muted">{c.help}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div>
            <label htmlFor="waive-gap-reason" className="type-meta">
              What did you check, and what did you find?
            </label>
            <textarea
              id="waive-gap-reason"
              ref={reasonRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-border bg-background p-2 type-body text-text-primary"
              placeholder="e.g. Searched GDELT, parish council minutes and the NRC docket back to 2015 — no coverage of this parcel."
              data-testid="waive-gap-reason"
            />
            {tooShort && (
              <p className="mt-1 type-body text-warning" data-testid="waive-gap-reason-hint">
                A later reader needs enough to judge the claim — at least {MIN_REASON} characters.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="waive-gap-cancel">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void submit()}
            disabled={!canSubmit}
            data-testid="waive-gap-confirm"
          >
            {saving ? 'Recording…' : 'Record waiver'}
          </Button>
        </div>
        <span className="sr-only" data-testid="waive-gap-requirement-id">{requirementId}</span>
      </div>
    </div>,
    document.body,
  );
};
